import { AgendaV3 } from "crqlar-react-common/dist/models/agenda/agenda.model";
import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow } from
"@mui/material";
import * as React from "react";
import { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { useLocalize } from "crqlar-react-common/dist/utils/text.util";
import {
  useServicesForSector } from
"crqlar-react-common/dist/utils/hooks/services/useServices";
import {
  useAllSectors } from
"crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {
  useBookingEngines } from
"crqlar-react-common/dist/utils/hooks/bookingEngine/useBookingEngineConfigs";
import CopyAdornment from "crqlar-react-common/dist/components/CopyAdornment";
import {
  useListAgendas } from
"crqlar-react-common/dist/utils/hooks/agenda/useAgendaData";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";

interface Props {
  agenda: AgendaV3;
  updateField: (fields: Partial<AgendaV3>) => void;
  nameMissing?: boolean;
}

const AgendaDetailsBasicTab: React.FC<Props> = ({ agenda, updateField, nameMissing }) => {
  const { t } = useLocalize();

  const allSectors = useAllSectors();
  const selectedSector = allSectors.find((s) => s.id === agenda.sectorId) || null;
  const services = useServicesForSector(selectedSector?.id);
  const bookingEngines = useBookingEngines();

  // initialize slug once on mount: use agenda.slug if present, otherwise generate once
  const [slug, setSlug] = useState<string>(agenda.slug || "");
  useEffect(() => {
    const existing = agenda.slug?.toString().trim();
    if (existing) {
      setSlug(existing);
    } else {
      const random = `agenda-${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`;
      setSlug(random);
      updateField({ slug: random });
    }
  }, [agenda.id]);


  const agendaQuery = useListAgendas();
  const agendas = agendaQuery.data ?? [];
  const slugExists = slug ?
  agendas.some((a) => a.slug?.toLowerCase().trim() === slug.toLowerCase().trim() && a.id !== agenda.id) :
  false;

  // Calculate service booking status
  const selectedServices = agenda.serviceIds?.
  map((id) => services.find((service) => service.id === id)).
  filter((service): service is NonNullable<typeof service> => service != null) || [];

  const selectedBookingEngines = agenda.bookingEngineIds?.
  map((id) => bookingEngines.find((engine) => engine.id === id)).
  filter((engine): engine is NonNullable<typeof engine> => engine != null) || [];

  // Sort services alphabetically by name
  const sortedServices = [...selectedServices].sort((a, b) =>
  (a.name || '').localeCompare(b.name || '')
  );

  // Create a map of service ID to booking engines
  const serviceToEnginesMap = new Map<string, typeof selectedBookingEngines>();
  sortedServices.forEach((service) => {
    const engines = selectedBookingEngines.filter((engine) =>
    engine?.serviceIds?.includes(service.id!)
    );
    serviceToEnginesMap.set(service.id!, engines);
  });

  return (
    <Stack gap={6}>
            <Stack gap={2}>
                <Typography variant="h5" textAlign="start">
                    {t("Basic info")}
                </Typography>

                <Stack gap={1}>
                    <Property
            title={t("Name")}
            tooltip={t("Display label for this agenda in setup and guest-facing planner screens.")}>

                        <TextField
              id="name"
              size="small"
              value={agenda.name ?? ''}
              onChange={(e) => updateField({ name: e.target.value })}
              fullWidth
              required
              error={nameMissing} />

                    </Property>

                    <Property
            title={t("Sector")}
            tooltip={t("Sector whose services can be scheduled in this agenda.")}>

                        <Autocomplete
              id="sector"
              options={allSectors}
              getOptionLabel={(option: any) => option?.name || ""}
              value={selectedSector}
              renderInput={(params) =>
              <TextField {...params} fullWidth size="small" />
              }
              fullWidth
              onChange={(e, value) =>
              updateField({ sectorId: value?.id, serviceIds: [] })
              } />

                    </Property>

                    <Property
            title={t("Slug")}
            tooltip={t("Unique public identifier used in agenda links and embeds.")}>

                        <TextField
              id="slug"
              size="small"
              value={slug}
              onChange={(e) => {
                const v = e.target.value;
                setSlug(v);
                updateField({ slug: v });
              }}
              fullWidth
              placeholder={t("my-agenda-slug")}
              error={slugExists}
              helperText={slugExists ? t("This slug is already used by another agenda. Use a differnt one.") : ""}
              InputProps={{
                endAdornment: <CopyAdornment value={slug || ""} />
              }} />

                    </Property>
                </Stack>
            </Stack>

            <Divider />

            <Stack gap={2}>
                <Typography variant="h5" textAlign="start">
                    {t("Configuration")}
                </Typography>

                <Stack gap={1}>
                <Property
            title={t("Enable Booking")}
            tooltip={t("Allows guests to book services from this agenda when the services are linked to a booking widget.")}>

                    <FormControlLabel
              control={
              <Switch
                checked={agenda.enableBooking ?? false}
                onChange={(e) => updateField({ enableBooking: e.target.checked })} />

              }
              label={agenda.enableBooking ? t("Guests can book services assigned to booking widget") : t("Guests cannot book any services. The agenda is accessibly in view-only mode.")} />

                </Property>

                <Property
            title={t("Booking Engines")}
            tooltip={t("Booking widgets that can make services on this agenda bookable.")}>

                    <Autocomplete
              id="bookingEngines"
              options={bookingEngines}
              multiple
              getOptionLabel={(option: any) => option?.name || ""}
              value={selectedBookingEngines}
              renderInput={(params) =>
              <TextField {...params} fullWidth size="small" />
              }
              fullWidth
              onChange={(e, value) =>
              updateField({ bookingEngineIds: value.map((v) => v?.id).filter((id): id is string => id != null) })
              } />

                </Property>

                <Property
            title={t("Services")}
            tooltip={t("Services shown on this agenda; services without a matching booking widget are view-only.")}>

                    <Stack direction="column" gap={1} width="100%"
            alignItems="flex-start">
                        <Autocomplete
                id="services"
                options={services}
                multiple
                getOptionLabel={(option: any) => option?.name || ""}
                value={selectedServices}
                renderInput={(params) =>
                <TextField {...params} fullWidth size="small" />
                }
                fullWidth
                onChange={(e, value) =>
                updateField({ serviceIds: value.map((v) => v?.id).filter((id): id is string => id != null) })
                } />


                        <Button
                onClick={() => {
                  const existingServiceIds = agenda.serviceIds || [];
                  const existingServicesSet = new Set(existingServiceIds);
                  const newServiceIds = services.
                  filter((s) => s.id && !existingServicesSet.has(s.id)).
                  map((s) => s.id!).
                  filter((id): id is string => id != null);
                  updateField({ serviceIds: [...existingServiceIds, ...newServiceIds] });
                }}>

                            {t("Add all services from sector")}
                        </Button>
                    </Stack>
                </Property>

                {sortedServices.length > 0 &&
          <Box sx={{ width: "100%" }}>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                                {t("Service")}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                                {t("Status")}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>
                                                {t("Assigned Booking Engines")}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedServices.map((service) => {
                    const engines = serviceToEnginesMap.get(service.id!) || [];
                    const isBookable = engines.length > 0;

                    return (
                      <TableRow key={service.id} hover>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {service.name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {isBookable ?
                          <Chip
                            icon={
                            <CheckCircleIcon />}
                            label={t("Bookable")}
                            size="small"
                            color="success"
                            variant="outlined" /> :


                          <Chip
                            icon={
                            <WarningIcon />}
                            label={t("View Only")}
                            size="small"
                            color="warning"
                            variant="outlined" />

                          }
                                                    </TableCell>
                                                    <TableCell>
                                                        {isBookable ?
                          <Stack direction="row"
                          flexWrap="wrap"
                          gap={0.5}>
                                                                {engines.map((engine) =>
                            <Chip
                              key={engine.id}
                              label={engine.name}
                              size="small"
                              variant="filled" />

                            )}
                                                            </Stack> :

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontStyle="italic">
                                                                {t("Not assigned to any booking engine")}
                                                            </Typography>
                          }
                                                    </TableCell>
                                                </TableRow>);

                  })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {sortedServices.some((s) => (serviceToEnginesMap.get(s.id!) || []).length === 0) &&
            <Typography variant="caption" color="text.secondary"
            sx={{ mt: 1, display: 'block' }}>
                                    {t("Services not assigned to any booking engine will be visible in view-only mode. Guests cannot book them.")}
                                </Typography>
            }
                    </Box>
          }
                </Stack>
            </Stack>
        </Stack>);

};

export default AgendaDetailsBasicTab;
